import { Input, Spin } from 'antd'
import { SearchOutlined, UserAddOutlined } from '@ant-design/icons'
import { Avatar, Badge, Button } from '@/components/ui'

export type UserSearchResult = {
  user_id: number
  name: string
  email: string
  mobile?: string
  designation?: string
  status: 'active' | 'inactive'
  assigned_to_event: boolean
  assigned_role?: 'owner' | 'crew'
}

interface UserSearchStepProps {
  query: string
  onQueryChange: (query: string) => void
  results: UserSearchResult[]
  searching: boolean
  /** True once a search has run for the current query and returned nothing */
  searched: boolean
  minQueryLength: number
  onSelect: (user: UserSearchResult) => void
  onCreateNew: () => void
}

const roleLabel = (role?: 'owner' | 'crew') =>
  role === 'owner' ? 'Event Owner' : 'Crew User'

/**
 * First step of the Add User drawer: look the person up before offering to
 * create anything, since accounts are shared across events.
 */
const UserSearchStep = ({
  query,
  onQueryChange,
  results,
  searching,
  searched,
  minQueryLength,
  onSelect,
  onCreateNew,
}: UserSearchStepProps) => {
  const tooShort = query.trim().length < minQueryLength

  return (
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-2">
        Search for an existing account
      </label>
      <Input
        value={query}
        onChange={(e) => onQueryChange(e.target.value)}
        placeholder="Search by name or email"
        prefix={<SearchOutlined className="text-black/25" />}
        allowClear
        autoFocus
      />
      <p className="mt-2 text-xs text-black/45">
        People keep one account across every event. Find them here so their existing account is
        assigned instead of duplicated.
      </p>

      <div className="mt-4">
        {searching && (
          <div className="py-8 text-center">
            <Spin size="small" />
          </div>
        )}

        {!searching && tooShort && (
          <p className="py-8 text-center text-sm text-black/45">
            Type at least {minQueryLength} characters to search.
          </p>
        )}

        {!searching && !tooShort && results.length > 0 && (
          <div className="divide-y divide-[#EBEBEB] border-t border-b border-[#EBEBEB]">
            {results.map((user) => (
              <button
                key={user.user_id}
                type="button"
                disabled={user.assigned_to_event}
                onClick={() => onSelect(user)}
                className="w-full flex items-center gap-3 px-1 py-3 text-left hover:bg-[#F3F5F8] disabled:hover:bg-transparent disabled:opacity-60 disabled:cursor-not-allowed transition-colors"
              >
                <Avatar name={user.name} size="small" />
                <div className="flex-1 min-w-0">
                  <div className="text-sm text-black/85 truncate">{user.name}</div>
                  <div className="text-xs text-black/45 truncate">{user.email}</div>
                </div>
                {user.assigned_to_event ? (
                  <Badge>{`Already ${roleLabel(user.assigned_role)}`}</Badge>
                ) : (
                  <span className="text-xs text-[#4277BB]">Select</span>
                )}
              </button>
            ))}
          </div>
        )}

        {!searching && !tooShort && searched && results.length === 0 && (
          <div className="py-6 text-center">
            <p className="text-sm text-black/85">No account found for &ldquo;{query.trim()}&rdquo;</p>
            <p className="mt-1 text-xs text-black/45">Create a new account for this person instead.</p>
          </div>
        )}
      </div>

      {!tooShort && !searching && (
        <Button
          variant="secondary"
          icon={<UserAddOutlined />}
          onClick={onCreateNew}
          className="mt-4"
        >
          Create new user
        </Button>
      )}
    </div>
  )
}

export default UserSearchStep
